import { addMinutes, subDays } from 'date-fns';
import { Response } from 'miragejs';
import {
  CheckCartResult,
  CreateOrderResult,
  DtaOrderDto,
  GetOrderResult,
  GetOrdersResult,
  GetOrdersSummaryResult,
  GetTicketsResult,
  OrderArticleDto,
  OrderDto,
  OrderPriceDto,
  OrderRestaurantDto,
  RestaurantServiceValues,
  RestaurantTypeValues,
  ScorpOrderDto,
  StatutCommandes,
  TicketDto,
  TicketItemDto,
} from '../api';
import { AppErrorKeys } from '../errors/error';
import { round } from '../utils';
import { arraySum } from '../utils/array-utils';
import { getErrorResponse } from './common';
import { getTodayWithHour } from './mock-restaurants';

/** TICKETS */
function createTicket(amount: number, tvaPercentage: number, date: Date, id: number, items: Array<TicketItemDto>, balance: number) {
  const ticket: TicketDto = {
    amount,
    legalData: {
      caisseId: 1,
      companyName: 'ELIOR RESTAURATION ET SERVICES​',
      companyAddress: "11 allée de l'Arche 92032 - Paris la Défense CEDEX​",
      companySiret: '31214777002213',
      intraCommunityVAT: 'FR92439681271',
      nafCode: '5629A',
    },
    siteName: 'Tour Égée',
    clientData: {
      balance: balance - amount,
      clientCode: '1008',
      numVentilation: undefined,
      oldBalance: balance,
    },
    date: date.toString(),
    id,
    items,
    payments: [],
    prices: [
      {
        ht: round(amount - amount * tvaPercentage * 0.01),
        label: tvaPercentage.toFixed(2) + '%',
        ttc: round(amount),
        tva: round(amount * tvaPercentage * 0.01),
      },
    ],
    totalTTC: amount,
  };
  return ticket;
}

const ITEMS: TicketItemDto[][] = [
  [
    {
      label: 'ASSIETTE GOURMANDE',
      price: 5.35,
    },
    {
      label: 'ECLAIR CHOCOLAT',
      price: 4.2,
    },
    {
      label: 'ADMISSION',
      price: 6.3,
    },
  ],
  [
    {
      label: 'BOEUF BOURGUIGNON',
      price: 5.22,
    },
    {
      label: 'YAHOURT NATURE',
      price: 1,
    },
    {
      label: 'ADMISSION',
      price: 6.3,
    },
  ],
  [
    {
      label: 'OMELETTE FORESTIERE',
      price: 5,
    },
    {
      label: 'CLAFOUTI AUX CERISES',
      price: 1.7,
    },
    {
      label: 'ADMISSION',
      price: 6.3,
    },
  ],
  [
    {
      label: 'EXPRESSO DA',
      price: 0.66,
    },
  ],
];

function createTickets() {
  const today = new Date();
  const tickets: TicketDto[] = [];
  let balance = 400;
  for (let i = 0; i < 13; i++) {
    const items = ITEMS[i % 4];
    const total = round(items.reduce((a, b) => a + b.price!, 0));
    tickets.push(createTicket(total, 20, subDays(today, 12 - i), i, items, balance));
    balance = round(balance - total);
  }
  return tickets.reverse();
}

export const MOCK_TICKETS_OK_PAGE0: GetTicketsResult = {
  hasMore: true,
  tickets: createTickets().slice(0, 9),
};

export const MOCK_TICKETS_OK_PAGE1: GetTicketsResult = {
  hasMore: false,
  tickets: createTickets().slice(10, 12),
};

export const MOCK_TICKETS = {
  OK_PAGE0: MOCK_TICKETS_OK_PAGE0,
  OK_PAGE1: MOCK_TICKETS_OK_PAGE1,
  KO: getErrorResponse(AppErrorKeys.AnErrorOccured),
};

/** ORDERS */

function createOrder(
  id: number,
  date: Date,
  creationDate: Date,
  restaurant: OrderRestaurantDto,
  daily: string,
  status: StatutCommandes,
  withCollectionEndDate: boolean,
  articles: OrderArticleDto[],
  price: OrderPriceDto,
  dta?: DtaOrderDto,
  scorp?: ScorpOrderDto,
) {
  const order: OrderDto = {
    date: addMinutes(date, 4).toISOString(),
    id,
    creationDate: creationDate.toISOString(),
    restaurant,
    price,
    dailyNumber: daily,
    number: daily + '-ABCD',
    lieuLabel: 'lieu label',
    status,
    collectionDate: scorp ? date.toISOString() : getTodayWithHour(12, 0),
    collectionEndDate: withCollectionEndDate ? getTodayWithHour(13, 0) : undefined,
    dta,
    scorp,
    articles,
    comments: 'Ne pas mettre trop de sel',
  };
  return order;
}

const dtaRestaurant: OrderRestaurantDto = {
  title: 'Titre Restaurant (DTA)',
  typeLabel: 'Dinner Takeaway',
  type: RestaurantTypeValues.DinnerTakeaway,
  isClickAndServe: false,
  openedAt: getTodayWithHour(9, 0),
  closedAt: getTodayWithHour(13, 0),
  hasCollectionTimeslots: false,
  isSAT: false,
  maxTableNumber: undefined,
};

const getCcRestaurant = (isClickAndServe: boolean, hasCollectionTimeslots: boolean): OrderRestaurantDto => ({
  title: 'Titre Restaurant (CC)',
  typeLabel: 'Click & Collect',
  type: RestaurantTypeValues.ClickAndCollect,
  isClickAndServe,
  openedAt: getTodayWithHour(10, 0),
  closedAt: getTodayWithHour(12, 0),
  hasCollectionTimeslots,
  isSAT: false,
  maxTableNumber: undefined,
});

const scorpRestaurant: OrderRestaurantDto = {
  title: 'Restaurant Scorp',
  typeLabel: 'Service de livraison 7j/7',
  type: RestaurantTypeValues.Scorp,
  hasCollectionTimeslots: false,
  isClickAndServe: false,
  isSAT: false,
  maxTableNumber: 10,
};

const ccArticles: OrderArticleDto[] = [
  {
    label: 'Article 1',
    quantity: 2,
    description: 'Article 1.1, Article 1.2, Article 1.3',
    price: 3.0,
  },
  {
    label: 'Article 2',
    quantity: 1,
    description: 'Article 2.1, Article 2.2, Article 2.3',
    price: 4.0,
  },
];

const dtaArticles: OrderArticleDto[] = [
  { label: 'Article 1', quantity: 1, description: 'Portion', price: 2.0 },
  { label: 'Article 2 no price no portion', quantity: 2 },
];

const getDtaData = (lockerCode?: string): DtaOrderDto => {
  return {
    lockerCode,
  };
};

function getScorpOrder(i: number) {
  const today = new Date();

  const totalArticles = arraySum(ccArticles.filter((x) => !!x.price).map((x) => x.price!));

  const tva = totalArticles * 0.1;
  const admission = 3;
  const ht = totalArticles - tva;

  const ttc = totalArticles + admission;
  const price: OrderPriceDto = {
    total: 1,
    admission: admission,
    ht,
    tva,
    ttc,
  };

  const collectionDate = subDays(today, i);
  const creationDate = subDays(collectionDate, 5);
  const limitDate = subDays(i === -3 ? new Date() : collectionDate, 1);

  return createOrder(
    i,
    collectionDate,
    creationDate,
    scorpRestaurant,
    `0001`,
    StatutCommandes.ScorpNonPayee,
    true,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ccArticles.map(({ description, ...x }) => {
      return x;
    }),
    price,
    undefined,
    { orderLimitDate: limitDate.toISOString(), service: RestaurantServiceValues.Lunch },
  );
}

function getCCOrder(i: number) {
  const today = new Date();

  const sousTotal = arraySum(ccArticles.filter((x) => !!x.price).map((x) => x.price!));

  const total = sousTotal + i;
  const price: OrderPriceDto = {
    sousTotal,
    total,
  };

  return createOrder(
    i,
    subDays(today, i),
    subDays(today, i),
    getCcRestaurant(i === 12, i === 10),
    `000${i + 1}`,
    i % 2 === 0 ? StatutCommandes.CcPayeeLivree : StatutCommandes.CcPayeeEnAttente,
    i == 10,
    ccArticles,
    price,
    undefined,
  );
}

function getDtaOrder(i: number) {
  const today = new Date();

  const totalArticles = arraySum(dtaArticles.filter((x) => !!x.price).map((x) => x.price!));

  const tva = totalArticles * 0.1;

  const ht = totalArticles - tva;
  const price: OrderPriceDto = {
    ht,
    tva,
    total: ht + tva,
  };

  return createOrder(
    i,
    subDays(today, i),
    subDays(today, i),
    dtaRestaurant,
    '1234381723812731',
    i % 2 === 0 ? StatutCommandes.DtaPayeeRetiree : StatutCommandes.DtaPayeeDisponible,
    false,
    dtaArticles,
    price,
    getDtaData('1234'),
  );
}

function createOrders() {
  const orders: OrderDto[] = [];

  for (let i = -6; i < 13; i++) {
    if (i <= 0) {
      orders.push(getScorpOrder(i));
    } else if (i < 6) {
      orders.push(getCCOrder(i));
    } else {
      orders.push(getDtaOrder(i));
    }
  }
  return orders;
}

const MOCK_ORDERS_OK_EXTERNAL: GetOrdersResult = {
  external: {
    isEmbedded: true,
    url: 'https://cw.mypi.net/cw/30211/01A54FC5022E61B0',
  },
};

const MOCK_ORDERS_OK_PAGE0: GetOrdersResult = {
  list: {
    hasMore: true,
    orders: createOrders().slice(0, 9),
  },
};

export const MOCK_ORDERS_OK_PAGE1: GetOrdersResult = {
  list: {
    hasMore: false,
    orders: createOrders().slice(9),
  },
};

export const MOCK_ORDERS = {
  EXTERNAL: MOCK_ORDERS_OK_EXTERNAL,
  OK_PAGE0: MOCK_ORDERS_OK_PAGE0,
  OK_PAGE1: MOCK_ORDERS_OK_PAGE1,
  KO: getErrorResponse(AppErrorKeys.AnErrorOccured),
};

const MOCK_ORDER_OK: GetOrderResult = {
  order: createOrders()[0],
};

export const MOCK_ORDER = {
  OK: MOCK_ORDER_OK,
  KO: getErrorResponse(AppErrorKeys.AnErrorOccured),
};

const MOCK_SCORP_ORDERS_OK: GetOrdersSummaryResult = {
  orders: [
    { date: new Date().toISOString(), orderId: 1, service: RestaurantServiceValues.Lunch },
    // { date: new Date().toISOString(), orderId: 2, service: RestaurantServiceValues.Dinner },
    // { date: new Date().toISOString(), orderId: 3, service: RestaurantServiceValues.Night },
  ],
  orderSettings: {
    availableServices: [RestaurantServiceValues.Night],
    cartMaxSingleItemAmount: 10,
    maxOrdersByDay: 10,
  },
};

export const MOCK_SCORP_ORDERS = {
  OK: MOCK_SCORP_ORDERS_OK,
};

const MOCK_CHECK_CART_OK: CheckCartResult = {
  totalTTC: 8.42,
  admission: 1,
  deliveryLocations: [
    { id: 1, label: 'Lieu A' },
    { id: 2, label: 'Lieu B' },
    { id: 3, label: 'Lieu C' },
  ],
};

const MOCK_CHECK_CART_ANTIGASPI_OK: CheckCartResult = {
  totalTTC: 5,
  admission: undefined,
  // deliveryLocations: [{ id: 1, label: 'Retrait AntiGaspi' }],
  deliveryLocations: [],
};

export const MOCK_CHECK_CART = {
  OK: MOCK_CHECK_CART_OK,
  OK_ANTIGASPI: MOCK_CHECK_CART_ANTIGASPI_OK,
  ERROR_OUT_OF_STOCK: getErrorResponse(AppErrorKeys.OutOfStock),
};

export const MOCK_CREATE_ORDER: { OK: CreateOrderResult; KO: Response } = {
  OK: { orderId: 1 },
  KO: getErrorResponse(AppErrorKeys.AnErrorOccured),
};
